import React, { useEffect, useState } from "react";
import { Button, Input, List, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { updateCurrentChat, addNewMessage } from "../../../../redux/slices/chatSlice";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/utils/Dispatcher";
import { customLogger } from "../../../../utils/Logger";
import { formatMessage } from "../../../../utils";
import ChatSidebarHeader from "../../../../components/common/ChatSidebarHeader";
import SearchInput from "../../../../components/common/UI/SearchInput";
import CustomLoader from "../../../../components/common/UI/Loader";
import ContactCard from "../../../../components/Chat/ContactCard";
import GradientButton from "../../../../components/common/UI/GradientButton";
import { storageService } from "../../../../services/storageService";
import ENDPOINTS from "../../../../services/endpoint";
import apiInstance from "../../../../services/apiService";
import "./index.scss";

interface NewGroupProps {
  handleBackClick: () => void;
  groupInfo: any;
}

interface Contact {
  _id: string;
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  iseFone: boolean;
  block?: any;
}

const AddNewMember: React.FC<NewGroupProps> = ({
  handleBackClick,
  groupInfo,
}) => {
  const dispatch = useAppDispatch();
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const [filteredContacts, setFilteredContacts] = useState<Contact[] | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [selectedNumberToUnblock, setSelectedNumberToUnblock] =
    useState<Contact | null>(null);
  const [efoneContactsList, setEfoneContactsList] = useState<Contact[] | null>(
    null
  );
  const userId = storageService.getUserId();

  const getContactsList = async () => {
    const URL = ENDPOINTS.CONTACTS_LIST;

    setLoading(true);
    try {
      const response = await apiInstance.get(URL);
      if (response.status === 200) {
        const contacts = response.data.data;
        const efoneContacts = contacts.filter(
          (contact: Contact) => contact.iseFone
        );
        setEfoneContactsList(efoneContacts);
      }
    } catch (err: any) {
      toast.error(err.msg || "Failed to get contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSelection = (contact: Contact, checked: boolean) => {
    if (contact.block) {
      setSelectedNumberToUnblock(contact);
      setShowBlockModal(true);
      return;
    }

    setSelectedContacts((prev: Contact[]) => {
      const exists = prev.some((c) => c._id === contact._id);
      if (checked && !exists) {
        return [...prev, contact];
      } else if (!checked && exists) {
        return prev.filter((c) => c._id !== contact._id);
      }
      return prev;
    });
  };

  const handleUnblockContact = async () => {
    setLoading(true);
    try {
      const response = await apiInstance.delete(
        `${ENDPOINTS.BLOCK_NUMBER}/${selectedNumberToUnblock?.block?._id}`
      );

      if (response.status === 200) {
        toast.success("Phone Number updated successfully!");
        setShowBlockModal(false);

        const updatedNumber: Contact = {
          _id: selectedNumberToUnblock!._id,
          fullName: selectedNumberToUnblock!.fullName,
          phoneNumber: selectedNumberToUnblock!.phoneNumber,
          emailAddress: selectedNumberToUnblock!.emailAddress,
          iseFone: selectedNumberToUnblock!.iseFone,
        };

        handleContactSelection(updatedNumber, true);
      }
    } catch (error) {
      customLogger(error);
      toast.error("There was an error updating");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedContacts.length) {
      toast.error("Please select at least one contact");
      return;
    }

    const URL = ENDPOINTS.MEMBER_UPDATE;

    const membersNumbers = selectedContacts.map(
      (contact) => contact.phoneNumber
    );

    setAddLoading(true);
    try {
      const response = await apiInstance.post(URL, {
        chatId: groupInfo.id,
        accountList: membersNumbers,
      });
      // debugger;
      if (response.status === 200) {
        const newAddedMembers = selectedContacts.map((contact) => ({
          isAdmin: false,
          phoneNumber: contact?.phoneNumber || "",
          _id: contact?._id,
        }));

        const existingMembers = currentChat?.groupMembers || [];

        const filteredNewMembers = newAddedMembers.filter(
          (newMember) =>
            !existingMembers.some(
              (existing: any) => existing._id === newMember._id
            )
        );

        dispatch(
          updateCurrentChat({
            groupMembers: [...existingMembers, ...filteredNewMembers],
          })
        );

        const newMsg = formatMessage(
          response?.data?.newMessage || response?.data?.conversation,
          userId
        );

        if (newMsg?.message) {
          dispatch(addNewMessage(newMsg));
        }

        getContactsList();
        toast.success(response.data.message || "Added successfully");
      }
    } catch (err: any) {
      console.error("Error removing member:", err);
      toast.error(err.message || "There was an error. Please try again");
    } finally {
      setAddLoading(false);
    }
  };

  useEffect(() => {
    getContactsList();
  }, []);

  useEffect(() => {
    if (!efoneContactsList) return;

    const groupMemberNumbers = groupInfo?.groupMembers?.map(
      (member: Contact) => member.phoneNumber
    );

    const filtered = efoneContactsList.filter(
      (contact) => !groupMemberNumbers?.includes(contact.phoneNumber)
    );

    // setEfoneContactsList(filtered);
    setFilteredContacts(filtered);
    setLoading(false);
  }, [efoneContactsList, groupInfo]);

  useEffect(() => {
    if (!filteredContacts) return;

    const query = searchQuery?.toLowerCase();
    const filtered = filteredContacts.filter(
      (contact: Contact) =>
        contact.fullName?.toLowerCase().includes(query) ||
        contact.phoneNumber.includes(query)
    );
    setFilteredContacts(filtered);
  }, [searchQuery]);

  return (
    <>
      <div className="add-new-member">
        <ChatSidebarHeader
          title="Add New Member"
          handleBackClick={handleBackClick}
        />

        <div className="hero-section">
          <div className="search-input-container">
            <SearchInput
              disabled={loading}
              prefix={<SearchOutlined />}
              placeholder="Search by name or phone number..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <div className="efone-contacts-list">
            <List
              dataSource={filteredContacts || []}
              loading={{
                spinning: loading,
                indicator: <CustomLoader />,
              }}
              renderItem={(contact: Contact) => {
                const isChecked = selectedContacts.some(
                  (selected) => selected.phoneNumber === contact.phoneNumber
                );
                return (
                  <ContactCard
                    key={contact._id}
                    userName={contact.fullName}
                    phoneNumber={contact.phoneNumber}
                    isEfone={contact.iseFone}
                    checkbox
                    checked={isChecked}
                    onChecked={(checked) =>
                      handleContactSelection(contact, checked)
                    }
                  />
                );
              }}
            />
          </div>

          <div className="btn-container">
            <GradientButton
              loading={addLoading}
              onClick={handleAddMembers}
              className="next-btn"
            >
              Add
            </GradientButton>
          </div>
        </div>
      </div>
      <Modal
        centered
        title={"Confirm Unblock"}
        open={showBlockModal}
        onCancel={() => {
          setShowBlockModal(false);
          setSelectedNumberToUnblock(null);
        }}
        footer={
          <>
            <Button
              className="modal-cancel-btn"
              type="text"
              onClick={() => setShowBlockModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="modal-ok-btn danger"
              onClick={handleUnblockContact}
              loading={loading}
            >
              Unblock
            </Button>
          </>
        }
        className="custom-modal"
      >
        <p className="text-primary">
          Are you sure you want to Unblock this chat?
        </p>
      </Modal>
    </>
  );
};

export default AddNewMember;
